/**
 * useCalendarWrite — écritures pour la création d'éléments depuis le Calendrier unifié.
 *
 * Cibles : appdata/ep:commevents, ep:tasks, ep:meetings, ep:publications,
 *          ep:eventeditors.
 * Structure V1 : { value: [...], updatedAt: Date.now() }.
 *
 * Chaque création écrit le tableau complet existant + le nouvel objet, en
 * respectant le schéma V1 exact pour rester lisible/éditable par l'app V1.
 * Aucune suppression destructive ici (hors retrait d'un événement ponctuel comm).
 */
import { doc, setDoc } from "firebase/firestore";
import { db } from "../../firebase";

function uid() {
  return String(Date.now()) + String(Math.random()).slice(2, 8);
}

export async function writeAppdata(key, value) {
  await setDoc(doc(db, "appdata", key), { value, updatedAt: Date.now() });
}

/* Normalise une sélection de clubs en tableau d'IDs (sans null/undefined). */
function clubIds(clubs) {
  return (Array.isArray(clubs) ? clubs : clubs != null ? [clubs] : []).filter(
    (c) => c !== null && c !== undefined && c !== ""
  );
}

/* ── Événement ponctuel comm — ep:commevents ──
 * Reprend la logique de saveEvent d'EvenementsV2 : un seul clubId (ou vide = national). */
export async function saveCommEvent(commEvents, form, appId, currentUser) {
  const now = new Date().toISOString();
  const list = Array.isArray(commEvents) ? commEvents : [];
  let next;
  if (form.id && list.some((e) => e.id === form.id)) {
    next = list.map((e) => (e.id === form.id ? { ...e, ...form, updatedAt: now } : e));
  } else {
    next = [
      ...list,
      {
        ...form,
        id: form.id || "ev" + uid(),
        createdBy: appId,
        createdByName: currentUser?.name
          || [currentUser?.firstName, currentUser?.lastName].filter(Boolean).join(" ")
          || "",
        createdAt: now,
        updatedAt: now,
      },
    ];
  }
  await writeAppdata("ep:commevents", next);
}

export async function deleteCommEvent(commEvents, id) {
  await writeAppdata("ep:commevents", (commEvents || []).filter((e) => e.id !== id));
}

/* ── Tâche — ep:tasks (schéma V1, cf. App.jsx createTask) ── */
export async function createTask(tasks, form, appId) {
  const now = new Date().toISOString();
  const newTask = {
    id: uid(),
    title: (form.title || "").trim(),
    description: form.description || "",
    clubs: clubIds(form.clubs),
    deadline: form.deadline || form.date || "",
    time: form.time || "",
    urgency: form.urgency || "Normal",
    assignedTo: clubIds(form.assignedTo),
    status: "À faire",
    recurrence: "Aucune",
    subtasks: [],
    notes: [],
    owner: appId,
    createdAt: now,
    history: [{ id: uid(), at: now, userId: appId, text: "Tâche créée depuis le calendrier" }],
    comments: [],
    timeEntries: [],
  };
  await writeAppdata("ep:tasks", [newTask, ...(tasks || [])]);
}

/* ── Réunion — ep:meetings (forme stockée : clubs[]/owner) ── */
export async function createMeeting(meetings, form, appId) {
  const now = new Date().toISOString();
  const newMeeting = {
    id: uid(),
    title: (form.title || "").trim(),
    date: form.date || "",
    time: form.time || "",
    endTime: form.endTime || "",
    duration: form.duration || "1h",
    location: form.location || "",
    type: form.type || "Présentiel",
    participants: clubIds(form.participants),
    clubs: clubIds(form.clubs),
    link: form.link || "",
    notesBefore: form.notesBefore || "",
    notesAfter: "",
    owner: appId,
    status: form.status || "",
    createdAt: now,
  };
  await writeAppdata("ep:meetings", [...(meetings || []), newMeeting]);
}

/* ── Publication — ep:publications (schéma V1, cf. App.jsx PublicationModal) ── */
export async function createPublication(publications, form, appId) {
  const newPub = {
    id: uid(),
    platform: form.platform || "Instagram",
    type: form.type || "Post",
    title: (form.title || "").trim(),
    status: form.status || "Brouillon",
    date: form.date || "",
    notes: form.notes || "",
    campagneId: form.campagneId || "",
    clubs: clubIds(form.clubs),
    owner: appId,
  };
  await writeAppdata("ep:publications", [...(publications || []), newPub]);
}

/* ── Autorisations événements ponctuels — ep:eventeditors ── */
export async function toggleEventEditor(eventEditors, userId) {
  const set = new Set((eventEditors || []).map(String));
  set.has(String(userId)) ? set.delete(String(userId)) : set.add(String(userId));
  await writeAppdata("ep:eventeditors", [...set]);
}
